import { useState } from 'react';

export default function ZoomableImage({ src, alt, className }: { src: string; alt?: string; className?: string }) {
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [lastRel, setLastRel] = useState({ x: 0, y: 0 });

    // Ghosting State
    const [overlaySrc, setOverlaySrc] = useState<string | null>(null);
    const [opacity, setOpacity] = useState(0.5);
    const [overlayPos, setOverlayPos] = useState({ x: 0, y: 0 }); // Relative to base
    const [overlayScale, setOverlayScale] = useState(1);
    const [editMode, setEditMode] = useState<'base' | 'overlay'>('base');

    const handleWheel = (e: React.WheelEvent) => {
        // Only prevent default if we're actually zooming to avoid scrolling issues
        const delta = -e.deltaY * 0.001;
        if (editMode === 'base') {
            setScale((prev) => Math.min(Math.max(1, prev + delta), 5));
        } else {
            setOverlayScale((prev) => Math.min(Math.max(0.5, prev + delta), 3));
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
        setLastRel({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!dragging) return;
        const dx = e.clientX - lastRel.x;
        const dy = e.clientY - lastRel.y;

        if (editMode === 'base') {
            setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
        } else {
            setOverlayPos((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
        }
        setLastRel({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
        setDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (ev) => {
                    setOverlaySrc(ev.target?.result as string);
                    setEditMode('overlay'); // Auto-switch to editing overlay
                };
                reader.readAsDataURL(file);
            }
        } else {
            const url = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
            if (url && (url.startsWith('http') || url.startsWith('data:'))) {
                setOverlaySrc(url);
                setEditMode('overlay');
            }
        }
    };

    return (
        <div
            className={`relative w-full h-[60vh] sm:h-[80vh] overflow-hidden bg-black cursor-move flex items-center justify-center group rounded-3xl border border-white/10 ${className || ''}`}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
        >
            {/* Base Layer */}
            <img
                src={src}
                alt={alt}
                className="absolute max-w-none transition-transform duration-75 ease-out select-none"
                style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    cursor: dragging ? 'grabbing' : 'grab'
                }}
                draggable={false}
            />

            {/* Ghost/Overlay Layer */}
            {overlaySrc && (
                <img
                    src={overlaySrc}
                    alt="Ghost Overlay"
                    className="absolute max-w-none transition-transform duration-75 ease-out select-none pointer-events-none"
                    style={{
                        transform: `translate(${position.x + overlayPos.x}px, ${position.y + overlayPos.y}px) scale(${scale * overlayScale})`,
                        opacity: opacity,
                        filter: 'sepia(100%) hue-rotate(180deg) saturate(200%) brightness(1.2)' // Cyan tint for ghost
                    }}
                    draggable={false}
                />
            )}

            {/* Controls Container */}
            <div className="absolute bottom-6 right-6 flex flex-col gap-3" onMouseDown={(e) => e.stopPropagation()}>
                {overlaySrc && (
                    <div className="bg-dark/90 backdrop-blur-xl p-4 rounded-3xl border border-white/10 mb-2 space-y-3 w-56 shadow-2xl">
                        <div className="flex justify-between text-[10px] font-black tracking-widest text-primary uppercase">
                            <span>Ghosting Engine</span>
                            <span>{Math.round(opacity * 100)}%</span>
                        </div>
                        <input
                            type="range"
                            min="0" max="1" step="0.1"
                            value={opacity}
                            onChange={(e) => setOpacity(parseFloat(e.target.value))}
                            className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-primary"
                        />
                        <p className="text-[9px] text-gray-400 italic leading-tight">Match your physical part to the blueprint schematic.</p>

                        <div className="flex justify-between items-center text-[10px] text-white mt-1 pt-2 border-t border-white/5">
                            <span className="font-bold uppercase tracking-widest text-gray-500">Edit Target</span>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => setEditMode('base')}
                                    className={`px-3 py-1 rounded-full text-[9px] font-black uppercase transition-all ${editMode === 'base' ? 'bg-primary text-dark shadow-lg shadow-primary/20' : 'bg-white/5 text-gray-400'}`}
                                >Base</button>
                                <button
                                    onClick={() => setEditMode('overlay')}
                                    className={`px-3 py-1 rounded-full text-[9px] font-black uppercase transition-all ${editMode === 'overlay' ? 'bg-primary text-dark shadow-lg shadow-primary/20' : 'bg-white/5 text-gray-400'}`}
                                >Ghost</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex gap-2 justify-end">
                    <button onClick={() => setScale(s => Math.min(s + 0.5, 5))} className="w-10 h-10 bg-dark/80 backdrop-blur-md border border-white/10 text-white rounded-full flex items-center justify-center hover:bg-primary hover:text-dark transition-all font-bold">+</button>
                    <button onClick={() => { setScale(1); setPosition({ x: 0, y: 0 }); setOverlayPos({ x: 0, y: 0 }); setOverlayScale(1); }} className="px-4 h-10 bg-dark/80 backdrop-blur-md border border-white/10 text-[10px] uppercase font-black tracking-widest text-white rounded-full hover:bg-white/10 transition-all">Reset Matrix</button>
                    <button onClick={() => setScale(s => Math.max(s - 0.5, 1))} className="w-10 h-10 bg-dark/80 backdrop-blur-md border border-white/10 text-white rounded-full flex items-center justify-center hover:bg-primary hover:text-dark transition-all font-bold">-</button>
                </div>
            </div>

            <div className={`absolute top-6 left-6 bg-primary/20 backdrop-blur-md border border-primary/30 text-primary text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-full transition-all pointer-events-none ${overlaySrc ? 'opacity-100 scale-100' : 'opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100'}`}>
                {overlaySrc ? 'Ghost Layer Entangled' : 'Drop Reference Image to Ghost'}
            </div>
        </div>
    );
}
